import React, { useContext, useEffect, useState } from 'react';
import { Button, Col, FormControl, Row } from 'react-bootstrap';
import Moment from 'react-moment';
import { faClock } from '@fortawesome/free-regular-svg-icons';
import {
    faCopy,
    faTimesCircle,
    faTrashAlt,
    faExclamationCircle,
    faChevronLeft,
    faPrint
} from '@fortawesome/free-solid-svg-icons';

import * as moment from 'moment';
import { useAlert } from 'react-alert';
import { useHistory } from 'react-router-dom';
import axios from 'axios';
import AuthContext from '../../../authentication/AuthContext';
import OrderSubmission from './submit/OrderSubmission';
import OrderContext from '../OrderContext';
import OrderStatusBanner from './OrderStatusBanner';
import RebateSummary from './rebate/RebateSummary';
import StatusModifier from '../../myorders/orders/statusmodifier/StatusModifier';
import UserContext from '../../../user/UserContext';
import { IconWrapper } from '../../../common/IconWrapper';
import OrderHeaderMoqProgress from './moq/OrderHeaderMoqProgress';

// Alternative to props that allows you to define which props to pass to a component.
// Also helps with IntelliJ autocomplete!
export default function OrderHeader({ setHideOnSubmit }) {
    let deadlineInSeconds;
    const { basket, orderId, orderHeader, refreshOrderInfo } = useContext(OrderContext);

    if (orderHeader.orderStatus !== 'DELETED') {
        deadlineInSeconds = orderHeader.deadline;
    }

    const cas = useContext(AuthContext);
    const { userDetails } = useContext(UserContext);

    const alert = useAlert();
    const history = useHistory();

    const [remainingTime, setRemainingTime] = useState();
    const [orderRef, setOrderRef] = useState('');
    const [showCopyOrder, setShowCopyOrder] = useState(false);
    const [showDeleteOrder, setShowDeleteOrder] = useState(false);
    const [showPostSubmission, setShowPostSubmission] = useState(false);
    const [showRebateDetails, setShowRebateDetails] = useState(false);

    useEffect(() => {
        let intervalId;
        if (deadlineInSeconds) {
            intervalId = setInterval(() => {
                deadlineInSeconds--;
                setRemainingTime(calculateDeadline(deadlineInSeconds));
                if (deadlineInSeconds <= 0) {
                    clearInterval(intervalId);
                }
            }, 1000);
        }
        return () => clearInterval(intervalId); // Cleanup function to stop interval when component is destroyed.
        // eslint-disable-next-line
    }, [orderHeader]);

    let cancelCopy = () => {
        setShowCopyOrder(false);
        setOrderRef('');
    };

    let submitCopyRequest = () => {
        setShowPostSubmission(false);
        setHideOnSubmit(false);

        let url = window.CONFIG['ws.activoc-web-order-api.orders.copy'];

        let requestBody = {
            origOrderId: orderId,
            retailerRef: orderRef
        };

        axios
            .post(url, requestBody, {
                headers: {
                    Authorization: cas,
                    'Content-Type': 'application/json'
                }
            })
            .then(response => {
                let createdOrderId = response.data.id;
                setShowCopyOrder(false);
                setOrderRef('');
                alert.success('Order ' + createdOrderId + ' successfully created');
                history.push('/order/' + createdOrderId);
                refreshOrderInfo();
            })
            .catch(error => {
                alert.error(error.response.data.message);
            });
    };

    let submitDeleteRequest = () => {
        let url = window.CONFIG['ws.activoc-web-order-api.delete.order'];
        url = url.replace('{orderId}', orderId);

        axios
            .delete(url, {
                headers: {
                    Authorization: cas,
                    'Content-Type': 'application/json'
                }
            })
            .then(() => {
                alert.success('Order ' + orderId + ' deleted');
                setShowDeleteOrder(false);
                refreshOrderInfo();
            })
            .catch(() => {
                alert.error('Unable to delete order, please try again');
            });
    };

    let getFile = (urlKey, errorMessage) => {
        axios
            .post(
                window.CONFIG[urlKey].replace('{id}', orderId),
                {},
                {
                    headers: {
                        Authorization: cas,
                        'Content-Type': 'application/json',
                        'X-Supplier-Code': orderHeader.supplierCode
                    }
                }
            )
            .then(response => {
                const fileURL = window.CONFIG['ws.activoc-web-order-api.orders.pdf.retrieve'].replace(
                    '{filename}',
                    response.data.filename
                );
                window.open(fileURL, '_blank');
            })
            .catch(() => {
                alert.error(errorMessage);
            });
    };

    return (
        <div className={'order-header-container'}>
            <h1>Order {orderId}</h1>
            <Row className={'order-info'}>
                <Col sm={3}>
                    <p>
                        <strong>Retailer ref: </strong> {orderHeader.retailerReference}
                    </p>
                    <p>
                        <strong>Supplier: </strong> {orderHeader.supplierName}
                    </p>
                    {orderHeader.supplierReference && (
                        <p>
                            <strong>Supplier order no: </strong> {orderHeader.supplierReference}
                        </p>
                    )}
                    <p>
                        <strong>Created: </strong>{' '}
                        <Moment format={'dddd DD/MM/YYYY HH:mm'}>{orderHeader.createdTimestamp}</Moment>
                    </p>
                </Col>
                <Col sm={3}>
                    <p>
                        <strong>Status: </strong> <StatusModifier orderHeader={orderHeader} />
                        <> </>
                        {orderHeader.paymentTakenAmount && (
                            <>(£{orderHeader.paymentTakenAmount} paid up front)</>
                        )}
                    </p>
                    <p>
                        <strong>Originating system: </strong> {orderHeader.originatingSystem}
                    </p>
                    <p>
                        <strong>Delivery day: </strong>{' '}
                        {orderHeader.deliveryDate ? (
                            <Moment format={'dddd DD/MM/YYYY'}>{orderHeader.deliveryDate}</Moment>
                        ) : (
                            'Next Available'
                        )}
                    </p>
                    {orderHeader.invoiceNumber && (
                        <p>
                            <strong>Invoice number: </strong> {orderHeader.invoiceNumber}
                        </p>
                    )}
                </Col>
                <Col sm={2} className={'totals'}>
                    <p>
                        <strong>Total lines: </strong> {orderHeader.totalLines}
                    </p>
                    <p>
                        <strong>Total cases: </strong> {orderHeader.totalCases}
                    </p>
                </Col>
                <Col sm={4} className={'totals'}>
                    <p>
                        <strong>Total cost: </strong>
                    </p>
                    <p className={'total-cost'}>{formatTotalCost(orderHeader.totalCost)}</p>
                </Col>
            </Row>
            <Row className={'order-info'}>
                <Col sm={8} />
                <Col sm={4} className={'totals'}>
                    <>
                        {deadlineInSeconds && orderHeader.orderStatus === 'OPEN' && (
                            <>
                                <p className={'clock flex'}>
                                    <IconWrapper icon={faClock} />
                                    <span className={'countdown'}>{remainingTime}</span> remaining
                                </p>
                                <p className={'remain'}>to review and complete this order</p>
                            </>
                        )}
                    </>
                </Col>
            </Row>
            <Row className={'order-info'}>
                <Col sm={12}>{orderHeader.orderStatus === 'OPEN' && <OrderHeaderMoqProgress />}</Col>
            </Row>

            <Row className={'button-row'}>
                <Col sm={5}>
                    {!showCopyOrder && !showDeleteOrder && (
                        <>
                            {!orderHeader.isContent && (
                                <Button
                                    variant={'secondary'}
                                    data-testid="orderCopyButton"
                                    disabled={orderHeader.totalLines <= 0 && basket.rejected.length === 0}
                                    onClick={() => setShowCopyOrder(true)}
                                >
                                    <IconWrapper icon={faCopy} />
                                    Copy
                                </Button>
                            )}
                            {orderHeader.orderStatus === 'OPEN' && (
                                <Button
                                    variant={'secondary'}
                                    data-testid="orderDeleteButton"
                                    onClick={() => setShowDeleteOrder(true)}
                                >
                                    <IconWrapper icon={faTrashAlt} />
                                    Delete
                                </Button>
                            )}
                        </>
                    )}
                    {window.CONFIG['activoc.invoice.enableDownload'] === 'true' && orderHeader.orderStatus === 'INVOICED' && (
                        <Button
                            variant={'secondary'}
                            data-testid="printInvoiceButton"
                            onClick={() =>
                                getFile(
                                    'ws.activoc-web-order-api.orders.invoice.pdf',
                                    'ERROR: Unable to print invoice.'
                                )
                            }
                        >
                            <IconWrapper icon={faPrint} />
                            Print Invoice
                        </Button>
                    )}
                    {(orderHeader.orderStatus === 'DELIVERED' || orderHeader.orderStatus === 'INVOICED') && (
                        <Button
                            variant={'secondary'}
                            data-testid="printEDNButton"
                            onClick={() =>
                                getFile(
                                    'ws.activoc-web-order-api.orders.delivery.pdf',
                                    'ERROR: Unable to print delivery note.'
                                )
                            }
                        >
                            <IconWrapper icon={faPrint} />
                            Print Delivery Note
                        </Button>
                    )}
                    {showPostSubmission && (
                        <>
                            <Button
                                variant={'secondary'}
                                data-testid="viewOrderButton"
                                onClick={() => {
                                    setShowPostSubmission(false);
                                    setHideOnSubmit(false);
                                }}
                            >
                                <IconWrapper icon={faChevronLeft} />
                                View Order
                            </Button>
                            <Button
                                variant={'secondary'}
                                data-testid="myOrdersButton"
                                onClick={() => history.push('/')}
                            >
                                <IconWrapper icon={faChevronLeft} />
                                My Orders
                            </Button>
                        </>
                    )}
                </Col>
                <Col sm={3} />
                <Col sm={4}>
                    {orderHeader.orderStatus === 'OPEN' && (
                        <OrderSubmission
                            onSubmitComplete={() => {
                                setShowPostSubmission(true);
                                setHideOnSubmit(true);
                                setShowRebateDetails(false);
                            }}
                        />
                    )}
                </Col>
            </Row>

            {showCopyOrder && (
                <Row className={'dialog-container'} data-testid={'copyOrderDialog'}>
                    <Col sm={6}>
                        <Row className={'dialog-header flex'}>
                            <p>
                                <IconWrapper icon={faExclamationCircle} />
                            </p>
                            <p>Are you sure you want to copy this order?</p>
                        </Row>
                        <Row className={'dialog-form'}>
                            <FormControl
                                type={'text'}
                                placeholder={'Give your order a name (optional)'}
                                value={orderRef}
                                data-testid={'orderCopyReference'}
                                onChange={e => setOrderRef(e.target.value)}
                                maxLength={45}
                            />
                            <span className={'character-count'}>
                                {orderRef === ''
                                    ? 'Max 45 characters'
                                    : 45 - orderRef.length + ' characters remaining'}
                            </span>
                        </Row>
                        <Row className={'dialog-buttons flex'}>
                            <Button variant={'secondary'} data-testid="orderCopyCancel" onClick={cancelCopy}>
                                <IconWrapper icon={faTimesCircle} />
                                Cancel
                            </Button>
                            <Button
                                variant={'primary'}
                                data-testid="orderCopySubmit"
                                onClick={submitCopyRequest}
                            >
                                <IconWrapper icon={faCopy} />
                                Copy Order
                            </Button>
                        </Row>
                    </Col>
                </Row>
            )}

            {showDeleteOrder && (
                <Row className={'dialog-container'} data-testid={'deleteOrderDialog'}>
                    <Col sm={6}>
                        <Row className={'dialog-header flex'}>
                            <p>
                                <IconWrapper icon={faExclamationCircle} />
                            </p>
                            <p>Are you sure you want to delete your order?</p>
                        </Row>
                        <Row className={'dialog-buttons delete-buttons flex'}>
                            <Button
                                variant={'secondary'}
                                data-testid="orderDeleteCancel"
                                onClick={() => setShowDeleteOrder(false)}
                            >
                                <IconWrapper icon={faTimesCircle} />
                                Cancel
                            </Button>
                            <Button
                                variant={'primary'}
                                data-testid="orderDeleteSubmit"
                                onClick={submitDeleteRequest}
                            >
                                <IconWrapper icon={faTrashAlt} />
                                Delete Order
                            </Button>
                        </Row>
                    </Col>
                </Row>
            )}

            {userDetails.currentFranchise &&
                userDetails.currentFranchise.rebates &&
                orderHeader.orderStatus === 'OPEN' && (
                    <RebateSummary showRebateDetailsParent={showRebateDetails} />
                )}

            <OrderStatusBanner showPostSubmission={showPostSubmission} />
        </div>
    );
}

function formatTotalCost(input) {
    return (
        <strong>
            £{Math.floor(input)}
            <sup>{(input % 1).toFixed(2).substring(1)}</sup>
        </strong>
    );
}

export function calculateDeadline(seconds) {
    let duration = moment.duration(seconds, 'seconds');
    let d = moment.duration(duration).days(),
        h = moment.duration(duration).hours(),
        m = moment.duration(duration).minutes(),
        s = moment.duration(duration).seconds();
    d = d.toString().length === 1 ? '0' + d : d;
    h = h.toString().length === 1 ? '0' + h : h;
    m = m.toString().length === 1 ? '0' + m : m;
    s = s.toString().length === 1 ? '0' + s : s;

    let remainingTimeString;
    if (d > 0) {
        remainingTimeString = d + 'd ' + h + 'h';
    } else if (m >= 5 || h > 0) {
        remainingTimeString = h + 'h ' + m + 'm';
    } else {
        remainingTimeString = m + 'm ' + s + 's';
    }
    return remainingTimeString;
}
